import { Space, Typography } from "antd"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { passwordStrength } from "../utils/validation/password-policy"
import Icon from "../Icon"

const { Text } = Typography
const alertIcon = "alert_triangle_error" as React.ComponentProps<
  typeof Icon
>["type"]

interface PasswordValidatorProps {
  isLogin?: boolean
  isSignup?: boolean
  error: string | Record<string, any> | null
  value: string | null
  setValidPass?: (valuse: boolean) => void
}

function PasswordValidator({
  value,
  error,
  isLogin = false,
  isSignup = false,
  setValidPass
}: PasswordValidatorProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(
    isLogin ? "At least 8 characters" : "Must be at least 8 characters"
  )
  const [errorText, setErrorText] = useState<string | null | boolean>(null)
  const [custom, setCustom] = useState({ status: "", color: "" })

  useEffect(() => {
    if (error && !isSignup) {
      if (typeof error === "string") {
        setErrorText(error)
      } else {
        setErrorText(
          error?.code === "token_not_valid"
            ? t("You have already created a password. Try to log in")
            : Object.values(error)[0]
        )
      }
    } else if (errorText) {
      setErrorText(false)
      checkValue(value)
    }
     
  }, [!!error])

  function defaultValue() {
    setText(isLogin ? "" : t("Must be at least 8 characters"))
    setCustom({ status: "", color: "" })
    if (isLogin && setValidPass) setValidPass(false)
  }

  useEffect(() => {
    if (value) {
      checkValue(value)
    } else {
      defaultValue()
    }
  }, [value])

  // Strength comes from the shared policy, so the meter can never call a
  // password Good that the server is about to reject.
  function checkValue(val: string | null) {
    if (!val) return

    const strength = passwordStrength(val)
    const meetsPolicy = strength !== null && strength !== "Weak"

    if (isLogin && setValidPass) setValidPass(meetsPolicy)

    if (strength === "Strong") {
      setText(isLogin ? "" : t("Your password is outstanding!"))
      setCustom({ status: "Strong", color: "green" })
    } else if (strength === "Good") {
      setCustom({ status: "Good", color: "yellow" })
    } else {
      setCustom({ status: "Weak", color: "red" })
    }
  }

  return (
    <div
      id="psw-validator"
      className={clsx(custom.color, {
        "login-password-validator": isLogin
      })}
    >
      {errorText ? (
        <Space size={4} className="error">
          {errorText && <Icon type={alertIcon} />}
          {typeof error === "object" && error?.code === "token_not_valid" ? (
            <span>
              {t("You have already created a password. Try to")}{" "}
              <a href="/login">{t("log in")}</a>
            </span>
          ) : (
            errorText || text
          )}
        </Space>
      ) : (
        <div className="wrap">
          {value ? (
            <>
              <div className="text-validator">{t(custom?.status)}</div>
              <div className="items-validator">
                <div />
              </div>
            </>
          ) : null}

          {!value ? (
            <Text type="secondary" style={{ lineHeight: 1.4 }}>
              {t(
                "Use at least 8 characters, including an uppercase letter, a lowercase letter and a number."
              )}
            </Text>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default PasswordValidator
